import { useImperativeHandle, useMemo } from 'react'
import {
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  formatDNI,
  useDatePicker,
  useInput,
  useSelect,
  useTextarea
} from '@chilecompra/react-kit'

import { CLAIM_TYPES } from '../../../config/settings/constants'
import { checkInputErrorProcessCode } from '../../../modules/utils/checkers'
import {
  formatAmountByCurrency,
  formatOnlyLettersNumbersAndSomeSymbols,
  formatProcessCode
} from '../../../modules/utils/formatters'

const BAD_REQUEST_ERROR_CODE = 400
const NOT_FOUND_ERROR_CODE = 404
const REASON_MAX_LENGTH = 3500

/**
 * Encapsulates all field hooks, validation, and exposed form API for ClaimsForm.
 */
export const useClaimsForm = ({
  claimReasons,
  claimTypes,
  onClaimTypeChange,
  onResetFormErrors,
  onResetOrganization,
  onResetOrganizationOrgName,
  organization,
  ref,
  submitError
}) => {
  const { error: claimReasonsError } = claimReasons
  const { error: claimTypesError } = claimTypes
  const { data: organizationData, error: organizationError } = organization

  const claimType = useSelect({
    changeCallback: updatedValue => {
      handleResetForm()

      if (organizationData?.dni || organizationData?.name) onResetOrganization()
      if (claimReasonsError || claimTypesError || organizationError || submitError)
        onResetFormErrors()

      onClaimTypeChange(updatedValue)

      if (updatedValue === '') {
        claimType.setValue(claimType.value)
        claimType.onError()
      }
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const processCode = useInput({
    changeCallback: updatedValue => {
      if (updatedValue !== '') return

      dni.onReset()
      orgLegalName.onReset()
      onResetOrganization()
    },
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorProcessCode()],
    formatCallbacks: [formatProcessCode]
  })

  const dni = useInput({
    changeCallback: updatedValue => {
      if (updatedValue !== '') return

      orgLegalName.onReset()
      onResetOrganizationOrgName()
    },
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatDNI],
    overwriteCallback: () => organizationData?.dni
  })

  const orgLegalName = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    overwriteCallback: () => organizationData?.name
  })

  const invoiceNumber = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const invoiceAmount = useInput({
    changeCallback: updatedValue => invoiceAmount.setValue(formatAmountByCurrency(updatedValue, 2)),
    errorCallbacks: [checkInputErrorRequired()]
  })

  const reason = useTextarea({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(REASON_MAX_LENGTH)],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const claimReason = useSelect({
    changeCallback: updatedValue => {
      if (reason.error) reason.onReset()

      if (updatedValue === '') {
        claimReason.setValue(claimReason.value)
        claimReason.onError()
      }
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const acceptanceDate = useDatePicker({
    errorCallbacks: [checkInputErrorRequired()]
  })

  const invoiceDate = useDatePicker({
    errorCallbacks: [checkInputErrorRequired()]
  })

  const isLatePayment = claimType.value === CLAIM_TYPES.LATE_PAYMENT
  const isProcurementIrregularity = claimType.value === CLAIM_TYPES.PROCUREMENT_IRREGULARITY

  const isFieldValid = field => Boolean(field.value) && !field.error

  const handleResetForm = () => {
    acceptanceDate.onReset()
    claimReason.onReset()
    dni.onReset()
    invoiceAmount.onReset()
    invoiceDate.onReset()
    invoiceNumber.onReset()
    orgLegalName.onReset()
    processCode.onReset()
    reason.onReset()
  }

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      acceptanceDate: acceptanceDate.value,
      claimReason: claimReason.value,
      claimType: claimType.value,
      invoiceAmount: invoiceAmount.value,
      invoiceDate: invoiceDate.value,
      invoiceNumber: invoiceNumber.value,
      organizationDni: dni.value,
      organizationName: orgLegalName.value,
      processCode: processCode.value,
      reason: reason.value
    }),

    reset: () => {
      onResetOrganization()
      handleResetForm()
      claimType.onReset()
      onResetFormErrors()
    },

    resetOrganizationDni: () => dni.onReset(),

    validate: () => {
      claimType.onError()
      processCode.onError()
      orgLegalName.onError()
      dni.onError()
      reason.onError()

      if (isProcurementIrregularity) claimReason.onError()

      if (isLatePayment) {
        acceptanceDate.onError()
        invoiceDate.onError()
        invoiceNumber.onError()
        invoiceAmount.onError()
      }

      const baseValid =
        isFieldValid(claimType) &&
        isFieldValid(processCode) &&
        isFieldValid(orgLegalName) &&
        isFieldValid(dni) &&
        isFieldValid(reason) &&
        reason.value.length <= REASON_MAX_LENGTH

      const procurementValid = !isProcurementIrregularity || isFieldValid(claimReason)

      const latePaymentValid =
        !isLatePayment ||
        [acceptanceDate, invoiceDate, invoiceNumber, invoiceAmount].every(isFieldValid)

      return baseValid && procurementValid && latePaymentValid
    }
  }))

  const claimTypeErrorText = useMemo(() => {
    if (claimType.error) return claimType.error
    if (claimTypesError) return 'Error al obtener el tipo de reclamo.'

    return ''
  }, [claimType.error, claimTypesError])

  const claimReasonErrorText = useMemo(() => {
    if (claimReason.error) return claimReason.error
    if (claimReasonsError) return 'Error al obtener el motivo de reclamo.'

    return ''
  }, [claimReason.error, claimReasonsError])

  const orgLegalNameErrorText = useMemo(() => {
    if (!processCode.value && orgLegalName.error) return orgLegalName.error

    if (!organizationError) return ''

    if (
      organizationError.code === NOT_FOUND_ERROR_CODE ||
      organizationError.code === BAD_REQUEST_ERROR_CODE
    ) {
      return 'El organismo no existe o no tiene información disponible.'
    }

    return 'Error al obtener información del organismo público.'
  }, [orgLegalName.error, organizationError, processCode.value])

  const isReasonTextareaDisabled =
    !claimType.value || organization.loading || (isProcurementIrregularity && !claimReason.value)

  return {
    acceptanceDate,
    claimReason,
    claimReasonErrorText,
    claimType,
    claimTypeErrorText,
    dni,
    invoiceAmount,
    invoiceDate,
    invoiceNumber,
    isLatePayment,
    isProcurementIrregularity,
    isReasonTextareaDisabled,
    orgLegalName,
    orgLegalNameErrorText,
    processCode,
    reason,
    REASON_MAX_LENGTH
  }
}
